import { useMemoizedFn } from 'ahooks';
import { Modal, Tabs } from 'antd';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { CompanyModalCloseIcon } from 'components/Icons';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { RevxSpin } from 'components/RevxSpin';
import { CartLineItem } from 'domains/cartDomain';
import { GALLERY_SKU_ENUM, GallerySku } from 'domains/gallerySkuDomain';
import { JobConfigViewer } from 'domains/jobconfigDomain';
import { useUser } from 'hooks';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { isBuyer, isSourcer, isSupplier } from 'utils/rbac';
import { SourceComparison } from '../../JobConfig/components/SourceComparison';
import { SupplierComparison } from '../../JobConfig/components/SupplierComparison';
import './CompanyModal.less';
import { JobConfigDetail } from './JobConfigDetail';
import cs from 'classnames';

interface Props {
  open: boolean;
  setOpen: (open: boolean) => void;
  sku?: GallerySku;
  setSku?: Dispatch<SetStateAction<GallerySku | undefined>>;
  isProject?: boolean;
}

export const CompanyModal = (props: Props) => {
  const { open, setOpen, sku, setSku, isProject } = props;
  const dispatch = useAppDispatch();
  const [jobConfigViewer, setJobConfigViewer] = useState<JobConfigViewer>();
  const galleryModalType = useAppSelector(
    (state) => state.gallery.galleryModalType
  );
  const [loading, setLoading] = useState<boolean>(false);

  const [type, setType] = useState<GALLERY_SKU_ENUM>(GALLERY_SKU_ENUM.SUMMARY);

  useEffect(() => {
    setType(galleryModalType);
  }, [galleryModalType, sku?.id]);

  const defaultJob = useMemoizedFn(async () => {
    try {
      setLoading(true);
      if (!sku) return;

      const resp = await dispatch.jobConfig.summerStartJobConfig({
        skuId: sku?.id!,
        quantity: sku?.defaultQty!,
      });
      setJobConfigViewer(resp);
    } catch {
      setOpen(false);
      if (typeof setSku === 'function') {
        setSku(undefined);
      }
    } finally {
      if (galleryModalType === GALLERY_SKU_ENUM.SUMMARY) {
        setLoading(false);
      }
    }
  });
  const user = useUser();

  const addToCArtHandler = useMemoizedFn(async (cartLineItem: CartLineItem) => {
    await dispatch.cart.getList();
    await dispatch.cart.updateCartItem(cartLineItem);
    setOpen(false);
  });
  useEffect(() => {
    setJobConfigViewer(undefined);
    defaultJob();
  }, [sku, defaultJob]);
  const hideCompare = useMemo(() => {
    return isBuyer(user) || sku?.unitPriceConfigured;
  }, [sku?.unitPriceConfigured, user]);
  return (
    <>
      <RevxSpin spinning={loading}>
        <Modal
          wrapClassName={cs('company-modal', {
            'is-project-modal': isProject,
          })}
          centered
          visible={open}
          onOk={() => setOpen(false)}
          onCancel={() => setOpen(false)}
          width={1386}
          closable={false}
          footer={null}
          destroyOnClose={true}
          forceRender={true}
          afterClose={() => {
            if (typeof setSku === 'function') {
              setSku(undefined);
            }
            if (jobConfigViewer) {
              dispatch.jobConfig.summeryDeleteJobConfig(jobConfigViewer.id);
            }
            dispatch.gallery.updateGalleryModalType(GALLERY_SKU_ENUM.SUMMARY);
          }}
        >
          <RevxFlexLayout
            justify="justify-center"
            align="center"
            className="company-modal-close"
            onClick={() => {
              dispatch.gallery.updateGalleryModalType(GALLERY_SKU_ENUM.SUMMARY);
              if (typeof setSku === 'function') {
                setSku(undefined);
              }
              if (jobConfigViewer) {
                dispatch.jobConfig.summeryDeleteJobConfig(jobConfigViewer.id);
              }
              setOpen(false);
            }}
          >
            <CompanyModalCloseIcon />
          </RevxFlexLayout>
          <Tabs
            activeKey={type}
            onChange={(e) => {
              setType(e as GALLERY_SKU_ENUM);
            }}
          >
            <Tabs.TabPane tab={sku?.name} key={GALLERY_SKU_ENUM.SUMMARY}>
              <JobConfigDetail
                jobConfigViewer={jobConfigViewer}
                open={open}
                setOpen={setOpen}
                addToCArtHandler={addToCArtHandler}
                sku={sku}
                isProject={isProject}
              />
            </Tabs.TabPane>
            {!hideCompare && (
              <Tabs.TabPane
                tab={
                  <>
                    <div
                      className="company-modal-dividingLine"
                      hidden={hideCompare}
                    ></div>
                    Compare
                  </>
                }
                key={GALLERY_SKU_ENUM.COMPARE}
              >
                <div style={{ padding: '0 100px' }}>
                  {Boolean(isSourcer(user)) && jobConfigViewer ? (
                    <SourceComparison
                      jobConfigViewer={jobConfigViewer}
                      addToCArtHandler={addToCArtHandler}
                      isProject={isProject}
                    />
                  ) : (
                    <></>
                  )}
                  {Boolean(isSupplier(user)) && jobConfigViewer ? (
                    <SupplierComparison
                      jobConfigViewer={jobConfigViewer}
                      addToCArtHandler={addToCArtHandler}
                      isProject={isProject}
                    />
                  ) : (
                    <></>
                  )}
                </div>
              </Tabs.TabPane>
            )}
          </Tabs>
        </Modal>
      </RevxSpin>
    </>
  );
};
